import moment from "moment";
import React, { useEffect, useRef } from "react";
import DataTable from "react-data-table-component";
import { useDispatch, useSelector } from "react-redux";

import { POUND_SIGN } from "@/Constants/common";
import {
  deSelectAllListings,
  fetchTerminatedShifts,
  setLoading,
  updateSelectedTerminatedShifts,
  updateTerminatedListSorting,
  updateTerminatedShiftsPagination,
} from "@/reducers/clientcontractdetail";
import {
  getActionByHtml,
  getColumnNormalHtml,
} from "@/utilities/datatable/columnFunctions";
import { sortByTime } from "@/utilities/DataTableSorting";
import useDebounceScroll from "@/utilities/hooks/useDebounceScroll";
import { commonFormatTime } from "@/utilities/utils";

import TableLoader from "../../TableLoader";
import TableNoContent from "../../TableNoContent";

const TerminatedShiftListing = () => {
  const {
    isTerminatedLoading,
    contractTerminatedShiftsLists,
    terminatedShiftsPagination,
    filters,
    contractId,
    toggleSelected,
    terminatedListSorting,
  } = useSelector((state) => state.clientContractDetail);
  const dispatch = useDispatch();
  const firstRender = useRef(true);
  useEffect(() => {
    // updating shifts on shift date change
    if (contractId == null) return;
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    dispatch(
      updateTerminatedShiftsPagination({
        ...terminatedShiftsPagination,
        currentPage: 1,
        isLastPage: false,
      }),
    );
    dispatch(
      fetchTerminatedShifts({
        contract_id: contractId,
        dates: filters.terminatesShiftDates.join(","),
      }),
    );
  }, [filters.terminatesShiftDates]);
  const columns = [
    {
      name: "Date",
      sortable: true,
      sortField: "contract_date",
      // sortFunction: (rowA, rowB) =>
      //   sortByDate(
      //     moment(rowA.contract_date, "DD/MM/YYYY hh:mm").format("DD/MM/YYYY"),
      //     moment(rowB.contract_date, "DD/MM/YYYY hh:mm").format("DD/MM/YYYY"),
      //   ),
      selector: (row) => getColumnNormalHtml(row.contract_date),
      width: "12%",
    },
    {
      name: "Start",
      sortable: true,
      sortField: "contract_start_time",
      selector: (row) =>
        getColumnNormalHtml(commonFormatTime(row.start_time, "HH:mm:ss")),
      width: "8%",
    },
    {
      name: "End",
      sortable: true,
      sortField: "contract_end_time",
      selector: (row) =>
        getColumnNormalHtml(commonFormatTime(row.end_time, "HH:mm:ss")),
      width: "8%",
    },
    {
      name: "Break",
      sortable: true,
      sortField: "un_paid_break_hour",
      sortFunction: (rowA, rowB) =>
        sortByTime(
          moment(rowA.break_time, "HH:mm:ss").format("HH:mm"),
          moment(rowB.break_time, "HH:mm:ss").format("HH:mm"),
        ),
      selector: (row) =>
        getColumnNormalHtml(commonFormatTime(row.break_time, "HH:mm:ss")),
      width: "7%",
    },
    {
      name: "Admin",
      sortable: true,
      sortField: "contract_admin_time",

      // sortFunction: (rowA, rowB) =>
      //   sortByTime(
      //     moment(rowA.admin_time, "HH:mm:ss").format("HH:mm"),
      //     moment(rowB.admin_time, "HH:mm:ss").format("HH:mm"),
      //   ),
      selector: (row) =>
        getColumnNormalHtml(commonFormatTime(row.admin_time, "HH:mm:ss")),
      width: "7%",
    },
    {
      name: "Unit",
      sortable: false,
      // sortFunction: (rowA, rowB) => sortByNumber(rowA, rowB, "unit"),
      selector: (row) => getColumnNormalHtml(row.unit ? row.unit : "-"),
      width: "7%",
    },
    {
      name: `Charge Rate (${POUND_SIGN})`,
      sortable: false,
      // sortFunction: (rowA, rowB) => sortByNumber(rowA, rowB, "shift_rate"),
      selector: (row) =>
        getColumnNormalHtml(
          `${POUND_SIGN}` + Number(`${row.shift_rate}`).toFixed(2),
        ),
      width: "12%",
    },
    {
      name: "Reason",
      sortable: false,
      // sortFunction: (rowA, rowB) => sortByText(rowA, rowB, "terminated_reason"),
      selector: (row) => getColumnNormalHtml(row?.terminated_reason),
      width: "19%",
    },
    {
      name: "Actioned By",
      sortable: false,
      selector: (row) => (
        <div className="flex flex-col" data-tag="allowRowEvents">
          {getActionByHtml(
            row.terminated_by || "-",
            moment(row.terminated_date, "YYYY-MM-DD HH:mm"),
            "",
          )}
        </div>
      ),
      width: "16%",
    },
  ];
  const dataTableRef = useRef();

  const handleDebounceScroll = useDebounceScroll(() =>
    dispatch(fetchTerminatedShifts({})),
  );
  useEffect(() => {
    if (dataTableRef.current) {
      const table = dataTableRef.current.querySelector("._DataTable");
      table.addEventListener("scroll", handleDebounceScroll);
      return () => {
        table.removeEventListener("scroll", handleDebounceScroll);
      };
    }
  }, [handleDebounceScroll]);

  const handleSort = ({ sortField: newSortField }) => {
    const { sortByColumn, sortByOrder } = terminatedListSorting; // get from Redux

    let newSortOrder = "asc";
    if (sortByColumn === newSortField) {
      newSortOrder = sortByOrder === "asc" ? "desc" : "asc";
    }

    dispatch(
      updateTerminatedListSorting({
        sortByColumn: newSortField,
        sortByOrder: newSortOrder,
      }),
    );
    dispatch(deSelectAllListings());
    dispatch(
      updateTerminatedShiftsPagination({
        ...terminatedShiftsPagination,
        currentPage: 1,
        isLastPage: false,
      }),
    );
    dispatch(setLoading(true));
    dispatch(fetchTerminatedShifts());
  };

  return (
    <>
      <div ref={dataTableRef}>
        <DataTable
          columns={columns}
          data={contractTerminatedShiftsLists}
          onSort={handleSort}
          selectableRows
          onSelectedRowsChange={(state) => {
            let selectedShifts = state.selectedRows.map((item) => {
              return item.shift_id;
            });
            dispatch(updateSelectedTerminatedShifts(selectedShifts));
          }}
          clearSelectedRows={toggleSelected}
          noDataComponent={<TableNoContent />}
          progressPending={isTerminatedLoading}
          progressComponent={<TableLoader rows={3} />}
          className="_DataTable"
          fixedHeader
          fixedHeaderScrollHeight={"600px"}
        />
      </div>
    </>
  );
};

export default TerminatedShiftListing;
